import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchCourseById, fetchCourseOffer, saveCourseOffer, deleteCourseOffer } from "./adminCourseService";
import "./adminCourses.css";

const fmt = (paise) => paise != null ? `₹${(paise / 100).toLocaleString("en-IN")}` : "—";

const EMPTY = { offerDescription: "", offerPercent: "", endDate: "", endTime: "" };

const AdminCourseOfferPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [course, setCourse]     = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [savedOffer, setSavedOffer] = useState(null); // full offer from API
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");

  const loadOffer = (courseData, offerData) => {
    setCourse(courseData);
    const o = offerData;
    if (o && o.endDate) {
      setSavedOffer(o);
      const dt = new Date(o.endDate);
      setForm({
        offerDescription: o.offerDescription || "",
        offerPercent:     o.offerPercent ?? "",
        endDate:          dt.toLocaleDateString("en-CA"),
        endTime:          dt.toTimeString().slice(0, 5),
      });
    } else {
      setSavedOffer(null);
    }
  };

  useEffect(() => {
    Promise.all([fetchCourseById(id), fetchCourseOffer(id)])
      .then(([cRes, oRes]) => loadOffer(cRes.data, oRes.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.offerDescription.trim()) return "Offer description is required.";
    if (!form.offerPercent || Number(form.offerPercent) <= 0 || Number(form.offerPercent) > 100)
      return "Offer percent must be between 1 and 100.";
    if (!form.endDate || !form.endTime) return "End date and time are required.";
    const end = new Date(`${form.endDate}T${form.endTime}`);
    if (isNaN(end.getTime())) return "Invalid end date/time.";
    if (end <= new Date()) return "End date must be in the future.";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); setSuccess(""); return; }
    setError(""); setSaving(true);
    try {
      const res = await saveCourseOffer(id, {
        offerDescription: form.offerDescription.trim(),
        offerPercent:     Number(form.offerPercent),
        endDate:          new Date(`${form.endDate}T${form.endTime}`).toISOString(),
      });
      setSavedOffer(res.data);
      setSuccess("Offer saved successfully.");
    } catch {
      setError("Failed to save offer. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this offer? It will no longer be visible to students.")) return;
    setError(""); setDeleting(true);
    try {
      await deleteCourseOffer(id);
      setSavedOffer(null);
      setForm(EMPTY);
      setSuccess("Offer removed.");
    } catch {
      setError("Failed to remove offer.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="ac-page"><div className="ac-loading">Loading…</div></div>;

  const hasOffer = Boolean(savedOffer);
  const isExpired = hasOffer && new Date(savedOffer.endDate) < new Date();

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => navigate("/admin/courses")}>
          ← Courses
        </button>
        <div>
          <h1 className="ac-page-title">Course Offer</h1>
          {course && <p className="ac-page-sub">{course.name}</p>}
        </div>
      </div>

      {hasOffer && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: isExpired ? "#fef2f2" : "#f0fdf4", border: `1px solid ${isExpired ? "#fca5a5" : "#86efac"}`, borderRadius: 8, fontSize: "0.85rem", color: isExpired ? "#dc2626" : "#15803d", fontWeight: 600 }}>
          {isExpired ? "⚠️ This offer has expired." : "✅ Active offer is live for students."}
        </div>
      )}

      {error   && <div className="ac-alert ac-alert-error">{error}</div>}
      {success && <div className="ac-alert ac-alert-success">{success}</div>}

      <div className="ac-form-card" style={{ maxWidth: 520 }}>
        <div className="ac-form-group">
          <label>Offer Description</label>
          <input
            className="ac-input"
            placeholder="e.g. Diwali Offer"
            value={form.offerDescription}
            onChange={(e) => set("offerDescription", e.target.value)}
          />
        </div>

        <div className="ac-form-group">
          <label>Discount %</label>
          <input
            className="ac-input"
            type="number" min="1" max="100"
            placeholder="e.g. 20"
            value={form.offerPercent}
            onChange={(e) => set("offerPercent", e.target.value)}
            style={{ maxWidth: 160 }}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="ac-form-group">
            <label>End Date</label>
            <input className="ac-input" type="date" value={form.endDate} onChange={(e) => set("endDate", e.target.value)} />
          </div>
          <div className="ac-form-group">
            <label>End Time</label>
            <input className="ac-input" type="time" value={form.endTime} onChange={(e) => set("endTime", e.target.value)} />
          </div>
        </div>

        {/* Read-only calculated preview */}
        {hasOffer && (
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 16px", marginBottom: 8 }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
              Calculated Discounted Prices
            </div>
            {course?.batchModeEnabled && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", marginBottom: 4 }}>
                <span style={{ color: "#374151" }}>Batch</span>
                <span>
                  <strong style={{ color: "#dc2626" }}>{fmt(savedOffer.offeredValueBatch)}</strong>
                  <span style={{ color: "#9ca3af", textDecoration: "line-through", marginLeft: 8 }}>{fmt(course.priceBatch)}</span>
                </span>
              </div>
            )}
            {course?.personalModeEnabled && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem" }}>
                <span style={{ color: "#374151" }}>Personal Coaching</span>
                <span>
                  <strong style={{ color: "#dc2626" }}>{fmt(savedOffer.offeredValuePersonal)}</strong>
                  <span style={{ color: "#9ca3af", textDecoration: "line-through", marginLeft: 8 }}>{fmt(course.pricePersonal)}</span>
                </span>
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <button className="ac-btn ac-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : hasOffer ? "Update Offer" : "Set Offer"}
          </button>
          {hasOffer && (
            <button className="ac-btn ac-btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Removing…" : "Remove Offer"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCourseOfferPage;
